import { useEffect, useId } from 'react'
import { formatInches } from '../../../lib/framingDimensions'
import useFramingPreviewImageStore from '../../../stores/useFramingPreviewImageStore'
import styles from './FramingPreview.module.css'
import FrameBorder from './FrameBorder'

const Artwork = ({ artwork, image, rect, clipId }) => {
  if (image.status !== 'ready') return <rect {...rect} fill="#f4f4f4" />
  const { crop } = artwork
  if (!crop || artwork.preCropped) {
    return (
      <image
        {...rect}
        href={artwork.url}
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#${clipId})`}
      />
    )
  }
  const viewBox = [
    (crop.x * image.width) / 100,
    (crop.y * image.height) / 100,
    (crop.width * image.width) / 100,
    (crop.height * image.height) / 100,
  ].join(' ')
  return (
    <svg
      {...rect}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid slice"
      overflow="hidden"
    >
      <image href={artwork.url} width={image.width} height={image.height} />
    </svg>
  )
}

const FramingPreview = ({ config }) => {
  const id = `framing-${useId().replace(/:/g, '')}`
  const ready = config?.status === 'ready'
  const isFloatCanvas = config?.construction === 'float-canvas'
  const isCanvas = isFloatCanvas || config?.construction === 'canvas'
  const textureUrl = ready ? config.frame?.assetUrl : null
  const photoUrl = ready ? config.artwork?.url : null
  const previewImages = useFramingPreviewImageStore((state) => state.previewImages)
  const loadPreviewImage = useFramingPreviewImageStore((state) => state.loadPreviewImage)
  const texture = textureUrl
    ? previewImages[textureUrl] || { status: 'loading' }
    : { status: 'empty' }
  const photo = photoUrl ? previewImages[photoUrl] || { status: 'loading' } : { status: 'empty' }

  useEffect(() => {
    loadPreviewImage(textureUrl)
    loadPreviewImage(photoUrl)
  }, [textureUrl, photoUrl, loadPreviewImage])

  const dimensions = ready ? config.dimensions : null
  const textureReady = ready && texture.status === 'ready'
  let message = config?.message
  if (ready && !config.frame?.assetUrl) message = 'Preview unavailable: the selected frame has no texture image.'
  else if (ready && texture.status === 'error') message = 'The frame image could not be loaded. Your order selections are unchanged.'
  else if (ready && texture.status === 'loading') message = 'Loading frame preview…'
  else if (textureReady && photo.status === 'error') message = 'The photograph could not be loaded. The frame dimensions are still shown.'
  else if (textureReady && photo.status === 'loading') message = 'Loading photograph…'
  else if (textureReady && photo.status === 'empty') message = 'Select a photograph to see it inside the frame.'

  return (
    <section className={styles.panel} aria-labelledby={`${id}-heading`}>
      <h3 id={`${id}-heading`} className={styles.heading}>
        Framing preview
      </h3>
      {textureReady && (
        <>
          <svg
            className={styles.preview}
            viewBox={`0 0 ${dimensions.outerWidth} ${dimensions.outerHeight}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-labelledby={`${id}-title ${id}-description`}
          >
            <title id={`${id}-title`}>{config.frame.name} framing preview</title>
            <desc id={`${id}-description`}>
              {config.artwork.name}. {isCanvas ? 'Canvas' : 'Print'} {formatInches(dimensions.printWidth)} by{' '}
              {formatInches(dimensions.printHeight)} inches. {isFloatCanvas ? 'Approximate' : 'Finished'} outside dimensions{' '}
              {formatInches(dimensions.outerWidth)} by {formatInches(dimensions.outerHeight)}{' '}
              inches.
            </desc>
            <defs>
              <clipPath id={`${id}-artwork`}>
                <rect {...dimensions.artwork} />
              </clipPath>
            </defs>
            <FrameBorder id={id} frame={config.frame} dimensions={dimensions} texture={texture}>
              <rect {...dimensions.mount} fill={config.matColor} />
              <Artwork
                artwork={config.artwork}
                image={photo}
                rect={dimensions.artwork}
                clipId={`${id}-artwork`}
              />
            </FrameBorder>
          </svg>
          <p className={styles.dimensions}>
            {isCanvas ? 'Canvas' : 'Print'}: {formatInches(dimensions.printWidth)} × {formatInches(dimensions.printHeight)} in
            {(!isCanvas || isFloatCanvas) && (
              <span>
                {isFloatCanvas ? (
                  <>Float gap: {formatInches(config.floatGapInches)} in (preview)</>
                ) : (
                  <>
                    Mat outside: {formatInches(dimensions.matWidth)} ×{' '}
                    {formatInches(dimensions.matHeight)} in
                  </>
                )}
              </span>
            )}
            <strong>
              {isFloatCanvas ? 'Approximate outside' : 'Finished outside'}: {formatInches(dimensions.outerWidth)} ×{' '}
              {formatInches(dimensions.outerHeight)} in
            </strong>
          </p>
          {config.batch && (
            <p className={styles.message}>
              The preview shows the selected photograph. Individual crops are not applied when
              adding all photos.
            </p>
          )}
        </>
      )}
      {message && (
        <p className={styles.message} role="status">
          {message}
        </p>
      )}
    </section>
  )
}

export default FramingPreview
